import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { IconMoreHorizontal } from '@components/icons/more-horizontal';
import { IconTravellerStatusCompleted } from '@components/icons/traveller-status-completed';
import { Horizontal, Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionSlow, transitionSlowest } from '@utils/transitions';

import { PlanTodoItem } from './plan-todo-item';

const DEFAULT_MAX_VISIBLE_TODOS = 4;

export type PlanTodoStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export type PlanTodo = {
  id: string;
  label: string;
  status: PlanTodoStatus;
  description?: string;
};

export type PlanProps = {
  id: string;
  title: string;
  description?: string;
  todos: PlanTodo[];
  maxVisibleTodos?: number;
  className?: string;
};

export const Plan = ({
  title,
  description,
  todos,
  maxVisibleTodos = DEFAULT_MAX_VISIBLE_TODOS,
}: PlanProps) => {
  const { t } = useScopedTranslate(locale);
  const [isShowingMore, setIsShowingMore] = useState(false);

  const { visibleTodos, hiddenTodos, completedCount, isAllComplete, progress } = useMemo(() => {
    const completed = todos.filter((todo) => {
      return todo.status === 'completed';
    }).length;

    return {
      visibleTodos: todos.slice(0, maxVisibleTodos),
      hiddenTodos: todos.slice(maxVisibleTodos),
      completedCount: completed,
      isAllComplete: completed === todos.length && todos.length > 0,
      progress: todos.length > 0 ? (completed / todos.length) * 100 : 0,
    };
  }, [todos, maxVisibleTodos]);

  const handleToggleMore = () => {
    setIsShowingMore((prev) => {
      return !prev;
    });
  };

  return (
    <PlanCard>
      <Horizontal justify="space-between" align="flex-start" space={4}>
        <Stack space={2}>
          <Text font="bold16" text={title} />
          {description && <Text font="regular14" color="textSecondary" text={description} />}
        </Stack>
        {isAllComplete && <IconTravellerStatusCompleted size={20} />}
      </Horizontal>

      <TodoContainer>
        <Stack space={2}>
          <Text
            font="regular14"
            color="textHelper"
            text={t('progressLabel', { completed: completedCount, total: todos.length })}
          />
          <ProgressBarContainer>
            <ProgressBarFill $progress={progress} $isComplete={isAllComplete} />
          </ProgressBarContainer>
        </Stack>

        <TodoList>
          <Todos items={visibleTodos} />

          {hiddenTodos.length > 0 && (
            <MoreToggleItem>
              <MoreToggle onClick={handleToggleMore}>
                <IconMoreHorizontal size={16} color="buttonIconPositive" />
                <Text
                  font="regular14"
                  color="textHelper"
                  text={t('moreLabel', { count: hiddenTodos.length })}
                />
              </MoreToggle>
              <Collapse isCollapsed={!isShowingMore} overflowType="visible">
                <TodoList>
                  <Todos items={hiddenTodos} />
                </TodoList>
              </Collapse>
            </MoreToggleItem>
          )}
        </TodoList>
      </TodoContainer>
    </PlanCard>
  );
};

const Todos = ({ items }: { items: PlanTodo[] }) => {
  return (
    <>
      {items.map((todo, index) => {
        const isFirst = index === 0;
        const isLast = index === items.length - 1;
        return <PlanTodoItem key={todo.id} todo={todo} isFirst={isFirst} isLast={isLast} />;
      })}
    </>
  );
};

const PlanCard = styled(Stack).attrs({
  space: 4,
})({
  width: '100%',
  maxWidth: pxToRem(576),
  minWidth: pxToRem(320),
  borderRadius: pxToRem(12),
  border: `1px solid ${theme.color.border10}`,
  background: theme.color.backgroundLightDark,
  padding: pxToRem(16),
});

const ProgressBarContainer = styled.div({
  height: pxToRem(6),
  borderRadius: pxToRem(3),
  background: theme.color.uiTrans10,
  overflow: 'hidden',
});

const ProgressBarFill = styled.div<{ $progress: number; $isComplete: boolean }>((p) => ({
  height: '100%',
  width: `${p.$progress}%`,
  background: p.$isComplete ? theme.color.elementGreen100 : theme.color.textPrimary,
  transition: `${transitionSlowest('width')}, ${transitionSlow('background-color')}`,
}));

const TodoContainer = styled(Stack).attrs({
  space: 3,
})({
  background: theme.color.backgroundLightDark,
  borderRadius: pxToRem(8),
  border: `1px solid ${theme.color.border10}`,
  padding: `${pxToRem(12)} ${pxToRem(16)}`,
});

const TodoList = styled.ul({
  listStyle: 'none',
  margin: 0,
  padding: 0,
});

const MoreToggleItem = styled(Stack).attrs({
  as: 'li',
  space: 2,
})({
  paddingTop: theme.space[1],
});

const MoreToggle = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 2,
})({
  padding: `${pxToRem(4)} 0`,
  borderRadius: pxToRem(6),
  cursor: 'pointer',

  '&:hover *': {
    color: theme.color.textPrimary,
  },
});

const locale = {
  en: {
    progressLabel: '{completed} of {total} complete',
    moreLabel: '{count} more',
  },
};
